import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { html } from 'lit';
import { AddLeadsModal } from '../add-leads-modal.js';
import { confirmDialog } from '../confirm-dialog.js';
import { toast } from '../toast.js';

/**
 * A campaign lead as returned by GET /api/campaigns/:id/leads. Only the
 * fields this panel renders — real docs carry more (statuses, timestamps, …)
 * which are passed through untouched.
 */
export interface ManagedLead {
  _id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  company?: string;
  jobTitle?: string;
  linkedinUrl?: string;
  status?: string;
}

export interface WizManageLeadsProps {
  campaignId: string;
  /** Campaign was originally sourced from Apollo — enables the top-up tab. */
  hasApolloSearch: boolean;
  leads: ManagedLead[];
  onLeadsChange: (leads: ManagedLead[]) => void;
}

/**
 * Leads step for a campaign that has already left 'draft': the leads are real
 * documents with generated messages, so instead of the one-shot CSV/Apollo
 * import tool (WizLeads) this panel manages them through the dedicated
 * endpoints — per-lead removal (deletes that lead's messages) and the shared
 * Add Leads modal (append/top-up endpoints generate the new messages). Both
 * directions work during the launch cool-down; every change restarts it.
 */
export const WizManageLeads = stateful((props: WizManageLeadsProps) => {
  const { campaignId, hasApolloSearch, leads, onLeadsChange } = props;

  const [addLeadsOpen, setAddLeadsOpen] = useState(false);
  const [deletingLeadId, setDeletingLeadId] = useState<string | null>(null);

  const refreshLeads = async () => {
    try {
      const resp = await fetch(`/api/campaigns/${encodeURIComponent(campaignId)}/leads`);
      const data = await resp.json();
      if (data.ok && Array.isArray(data.leads)) onLeadsChange(data.leads);
    } catch {
      /* keep the current list — the next full load will catch up */
    }
  };

  const deleteLead = async (lead: ManagedLead) => {
    const confirmed = await confirmDialog(
      `Delete ${lead.firstName ?? ''} ${lead.lastName ?? ''} and all generated messages for this lead?`,
      { title: 'Delete lead', confirmLabel: 'Delete', confirmClass: 'btn-error' },
    );
    if (!confirmed) return;
    setDeletingLeadId(lead._id);
    try {
      const resp = await fetch(
        `/api/campaigns/${encodeURIComponent(campaignId)}/leads/${encodeURIComponent(lead._id)}`,
        { method: 'DELETE' },
      );
      const data = await resp.json();
      if (data.ok) {
        onLeadsChange(leads.filter(l => l._id !== lead._id));
        toast.success('Lead removed.');
      } else {
        toast.error(data.error ?? 'Delete failed');
      }
    } catch {
      toast.error('Delete request failed');
    }
    setDeletingLeadId(null);
  };

  return html`
    <div class="flex flex-col gap-4">
      <div class="card bg-base-100 shadow-sm">
        <div class="card-body">
          <div class="flex items-center justify-between">
            <h2 class="card-title text-lg">Leads (${leads.length})</h2>
            <button class="btn btn-outline btn-sm gap-1.5" @click=${() => setAddLeadsOpen(true)}>
              <span class="icon-[material-symbols--person-add] text-base"></span>
              Add Leads
            </button>
          </div>
          <p class="text-base-content/60 text-sm">
            These leads already have generated messages. Removing a lead deletes its messages; added leads get
            theirs generated automatically. During the launch cool-down every change restarts the hour.
          </p>
          ${
            leads.length === 0
              ? html`<p class="text-base-content/50 py-4 text-center">No leads in this campaign</p>`
              : html`
                <div class="overflow-x-auto">
                  <table class="table-sm table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Company</th>
                        <th>Email</th>
                        <th>LinkedIn</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      ${leads.map(
                        l => html`
                          <tr class="hover">
                            <td>
                              <div class="font-medium">${l.firstName ?? ''} ${l.lastName ?? ''}</div>
                              <div class="text-base-content/50 text-xs">${l.jobTitle ?? ''}</div>
                            </td>
                            <td>${l.company ?? ''}</td>
                            <td>${l.email || html`<span class="text-base-content/40">—</span>`}</td>
                            <td>
                              ${
                                l.linkedinUrl
                                  ? html`<span class="icon-[material-symbols--check] text-success text-base"></span>`
                                  : html`<span class="text-base-content/40">—</span>`
                              }
                            </td>
                            <td class="text-right">
                              <button
                                class="btn btn-ghost btn-xs text-error ${
                                  deletingLeadId === l._id ? 'btn-disabled' : ''
                                }"
                                title="Remove lead"
                                @click=${() => void deleteLead(l)}
                              >
                                ${
                                  deletingLeadId === l._id
                                    ? html`<span class="loading loading-spinner loading-xs"></span>`
                                    : html`<span class="icon-[material-symbols--delete] text-base"></span>`
                                }
                              </button>
                            </td>
                          </tr>
                        `,
                      )}
                    </tbody>
                  </table>
                </div>
              `
          }
        </div>
      </div>

      ${AddLeadsModal({
        campaignId,
        hasApolloSearch,
        open: addLeadsOpen,
        onClose: () => setAddLeadsOpen(false),
        onLeadsAdded: refreshLeads,
      })}
    </div>
  `;
});
